import { ReactPlugin } from "@microsoft/applicationinsights-react-js"
import { ApplicationInsights } from "@microsoft/applicationinsights-web"
import { createBrowserHistory } from "history"
import { createAppInsightsConfig, isAppInsightsEnabled } from "./appInsightsConfig"
import { Id, toast, ToastContent, ToastOptions } from "react-toastify"

// @ts-ignore
const browserHistory = createBrowserHistory({ basename: "" })
export const reactPlugin = new ReactPlugin()
const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING?.trim() ?? ""
const enabled = isAppInsightsEnabled(connectionString, import.meta.env.VITE_ENABLE_APPINSIGHTS)

export const appInsights = new ApplicationInsights({
    config: createAppInsightsConfig(connectionString, enabled, browserHistory, reactPlugin),
})
if (enabled) {
    appInsights.loadAppInsights()
}

// Function to update accountId after MSAL is initialized
export const updateAppInsightsAccount = (msalInstance: { getActiveAccount: () => any }) => {
    const activeAccount = msalInstance.getActiveAccount()
    if (activeAccount) {
        // appInsights.config.accountId = activeAccount.localAccountId
        // appInsights.context.user.id = activeAccount.localAccountId
        appInsights.setAuthenticatedUserContext(activeAccount.localAccountId)

        // console.log("Updated App Insights account ID:", activeAccount.localAccountId)
    }
    appInsights.trackPageView()
    appInsights.trackEvent({ name: "App loaded" })
}

// Helper function to track telemetry based on severity
const trackTelemetry = (message: string, severity: "error" | "warning" | "information") => {
    switch (severity) {
        case "error":
            try {
                appInsights.trackException({ exception: new Error(message) })
            } catch (e) {
                // Do nothing on error
            }

            break
        case "warning":
            try {
                appInsights.trackTrace({ message, severityLevel: 2 })
            } catch {
                // Do nothing on error
            }
            break
        case "information":
            try {
                appInsights.trackTrace({ message, severityLevel: 1 })
            } catch {
                // Do nothing on error
            }
            break
    }
}

const getNativeConsoleMethod = (method: "error" | "warn" | "info" | "log") => {
    const consoleObject = globalThis.console
    if (!consoleObject) {
        return null
    }

    const native = consoleObject[method]
    if (typeof native !== "function") {
        return null
    }

    try {
        return native.bind(consoleObject)
    } catch {
        return null
    }
}

const invokeSafely = (fn: unknown, context: unknown, args: unknown[]) => {
    if (typeof fn !== "function") {
        return
    }

    try {
        Reflect.apply(fn as (...fnArgs: unknown[]) => void, context, args)
    } catch {
        // Telemetry must never break the app
    }
}

const formatConsoleArgs = (args: unknown[]): string => {
    try {
        return args
            .map(arg => {
                if (typeof arg === "object" && arg !== null) {
                    try {
                        return JSON.stringify(arg)
                    } catch {
                        return String(arg)
                    }
                }
                return String(arg)
            })
            .join(" ")
    } catch {
        return "[unserializable console message]"
    }
}

// Wrap console methods
const consoleMethodsToTrack: ("error" | "warn" | "info" | "log")[] = ["error", "warn", "info", "log"]

consoleMethodsToTrack.forEach(method => {
    const originalMethod = getNativeConsoleMethod(method)
    console[method] = function (...args: any[]) {
        try {
            const message = formatConsoleArgs(args)
            if (message.includes("Warning: useLayoutEffect does nothing on the server")) {
                return
            }
            invokeSafely(originalMethod, globalThis.console, args)

            switch (method) {
                case "error":
                    trackTelemetry(message, "error")
                    break
                case "warn":
                    trackTelemetry(message, "warning")
                    break
                case "info":
                case "log":
                    trackTelemetry(message, "information")
                    break
            }
        } catch {
            // Telemetry must never break the app
        }
    }
})

// Wrap toast methods
const toastMethodsToTrack: ("error" | "warn" | "info" | "success")[] = ["error", "warn", "info", "success"]

toastMethodsToTrack.forEach(method => {
    const originalMethod = toast[method]
    toast[method] = function <TData = unknown>(content: ToastContent<TData>, options?: ToastOptions<TData>) {
        let result: Id | undefined
        try {
            if (typeof originalMethod === "function") {
                result = Reflect.apply(originalMethod, toast, [content, options]) as Id
            }

            const message = typeof content === "string" ? content : JSON.stringify(content)

            switch (method) {
                case "error":
                    trackTelemetry(message, "error")
                    break
                case "warn":
                    trackTelemetry(message, "warning")
                    break
                case "info":
                case "success":
                    trackTelemetry(message, "information")
                    break
            }
        } catch {
            // Telemetry must never break the app
        }

        return result as Id
    }
})
